/**
 * Document Store - Zustand store for document tab management in Desk
 *
 * Manages which documents are open, which is active, and tab operations.
 * Open tabs and active document are persisted to localStorage across page loads.
 *
 * @example
 * const openDocuments = useDocumentStore((state) => state.openDocuments)
 * const openDocument = useDocumentStore((state) => state.openDocument)
 *
 * // Open a document (adds to tabs if not already open)
 * openDocument('doc-123')
 */

import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'
// Plan 42 Task 8 — runtime-only cross-store reference for the openDocument
// guard. ES modules tolerate this circular dep because deskStore.ts is only
// read via getState() inside the action body (lazy), never at module init.
import { useDeskStore } from './deskStore'

// Maximum number of open documents to prevent memory issues
const MAX_OPEN_DOCUMENTS = 10

export interface OpenDocument {
  id: string
  name: string
  // Plan 155: 'workbench' opens a workbench precedent in the desk middle pane
  // (read-only viewer). Hooks keyed to matter documents must guard against it.
  // Plan 167: 'workflow-result' removed — clicking a workflow doc now activates
  // a layer on the source doc rather than opening a separate tab. The
  // documentStore persist migration (version 1) filters stale tabs from
  // localStorage. The `workflowType` field is retained nominally because
  // the persisted shape (pre-167) may carry it; consumers should ignore it.
  // Plan 183: 'drafting-workflow' renders a streaming clause-card view for a
  // drafting analysis_run. `id` is the parent analysis_run_id (same convention
  // as the WORKFLOW DOCS click handler).
  type: 'document' | 'notes' | 'playbook' | 'workbench' | 'drafting-workflow'
  matterId: string
  openedAt: number
  workflowType?: 'review' | 'compare' | 'drafting' | 'negotiation' | null
}

// Task 220: Selected clause for context-aware quick actions
// Plan 40 / #794: documentId scopes the selection to its owning tab so close
// paths can clear precisely instead of leaving the selection orphaned when
// its document is closed.
export interface SelectedClause {
  id: string
  text: string
  title: string
  documentId: string
}

// Saved tab state per matter
interface MatterTabSnapshot {
  openDocuments: OpenDocument[]
  activeDocumentId: string | null
}

export interface DocumentStore {
  // Document tabs
  openDocuments: OpenDocument[]
  activeDocumentId: string | null

  // Per-matter saved tab state
  matterTabState: Record<string, MatterTabSnapshot>

  // Task 220: Selected clause for context-aware quick actions
  selectedClause: SelectedClause | null

  // Plan 171: when true, the focal clause's descendants join the selection
  // set used by ClauseActionsView / ClauseChatThread.buildContextInjection.
  // Reset to false whenever the focal clause changes via selectClause /
  // clearClauseSelection. The atomic selectSection action below sets focal
  // AND this flag in a single setter, used by the section header "Select
  // section" button.
  selectedClauseIncludeDescendants: boolean

  // Task 238: Pending clause to scroll to after document loads
  pendingClauseId: string | null

  // Plan 191 / Issue #796: scroll-only navigation primitive. Unlike
  // pendingClauseId (which routes through handleSelectClause and CLEARS
  // focusedIssueId at Desk.tsx:669), this primitive only scrolls — neither
  // selection nor focused issue is touched. Used by IssueAnnotation mirror
  // clicks so the just-opened issue detail survives the scroll.
  scrollToClauseId: string | null

  // Actions
  openDocument: (doc: Omit<OpenDocument, 'openedAt'>) => void
  closeDocument: (id: string) => void
  setActiveDocument: (id: string) => void
  closeAllDocuments: () => void
  closeOtherDocuments: (keepId: string) => void
  closeMatterDocuments: (matterId: string) => void
  switchMatter: (fromMatterId: string, toMatterId: string) => void
  // Plan 42 Task 2 — mount-time validation primitive. Drops live tabs whose
  // matterId doesn't match `matterId`. When `matterId` is null, hard-clears
  // every tab (matterId-string === null never holds; relies on identity check
  // not equality to defend against malformed persisted state).
  validateLiveTabs: (matterId: string | null) => void
  reorderDocuments: (fromIndex: number, toIndex: number) => void
  // Task 220: Clause selection
  selectClause: (clause: SelectedClause | null) => void
  clearClauseSelection: () => void
  // Plan 171: section selection — atomic focal + includeDescendants=true.
  selectSection: (clause: SelectedClause | null) => void
  setSelectedClauseIncludeDescendants: (value: boolean) => void
  // Task 238: Set pending clause to scroll to after document loads
  setPendingClauseId: (clauseId: string | null) => void
  // Plan 191 / Issue #796: scroll-only (no selection, no focus clearing).
  setScrollToClauseId: (clauseId: string | null) => void
}

export const useDocumentStore = create<DocumentStore>()(
  devtools(
    persist(
    (set, get) => ({
      // Initial state
      openDocuments: [],
      activeDocumentId: null,
      matterTabState: {},
      selectedClause: null, // Task 220
      selectedClauseIncludeDescendants: false, // Plan 171
      pendingClauseId: null, // Task 238
      scrollToClauseId: null, // Plan 191

      // Actions
      openDocument: (doc) => set(
        (state) => {
          // Plan 42 Task 8 — reject tabs whose matterId disagrees with the
          // currently-selected matter. CRITICAL: do NOT add a `doc.matterId &&`
          // truthy clause — empty-string matterIds (from existing
          // `selectedMatterId || ''` patterns) are exactly the bad stamps this
          // invariant must reject. Empty matterId is never a legitimate value
          // when a matter is selected.
          const currentMatter = useDeskStore.getState().selectedMatterId
          if (currentMatter && doc.matterId !== currentMatter) {
            // eslint-disable-next-line no-console
            console.warn(
              `[documentStore.openDocument] refusing tab whose matterId=${doc.matterId || '(empty)'} ` +
              `does not match selectedMatterId=${currentMatter}`,
              { doc },
            )
            return state
          }

          // Check if already open
          const existingIndex = state.openDocuments.findIndex(d => d.id === doc.id)

          if (existingIndex !== -1) {
            // Already open, just make it active
            return { activeDocumentId: doc.id }
          }

          // Add new document
          const newDoc: OpenDocument = {
            ...doc,
            openedAt: Date.now(),
          }

          let newDocuments = [...state.openDocuments, newDoc]

          // Enforce max limit by closing oldest documents
          if (newDocuments.length > MAX_OPEN_DOCUMENTS) {
            // Sort by openedAt, remove oldest (but not the new one)
            const sorted = newDocuments
              .slice(0, -1) // Exclude the new doc
              .sort((a, b) => a.openedAt - b.openedAt)

            // Remove the oldest
            const toRemove = sorted[0]
            newDocuments = newDocuments.filter(d => d.id !== toRemove.id)
          }

          return {
            openDocuments: newDocuments,
            activeDocumentId: doc.id,
          }
        },
        false,
        'openDocument'
      ),

      closeDocument: (id) => set(
        (state) => {
          const newDocuments = state.openDocuments.filter(d => d.id !== id)

          // If we're closing the active document, activate the next one
          let newActiveId = state.activeDocumentId
          if (state.activeDocumentId === id) {
            const closedIndex = state.openDocuments.findIndex(d => d.id === id)
            // Try to activate the next document, or the previous, or null
            newActiveId = newDocuments[closedIndex]?.id
              ?? newDocuments[closedIndex - 1]?.id
              ?? null
          }

          // Plan 40 / #794: selectedClause is document-scoped — clear only
          // when the closed tab owned it. pendingClauseId has no documentId
          // pair (one-shot scroll target consumed by Desk.tsx); clear it
          // unconditionally — worst case the user re-clicks the citation.
          const closedSelected = state.selectedClause?.documentId === id
          return {
            openDocuments: newDocuments,
            activeDocumentId: newActiveId,
            pendingClauseId: null,
            scrollToClauseId: null,
            ...(closedSelected && {
              selectedClause: null,
              selectedClauseIncludeDescendants: false,
            }),
          }
        },
        false,
        'closeDocument'
      ),

      setActiveDocument: (id) => set(
        (state) => {
          // Only set active if the document is actually open
          const isOpen = state.openDocuments.some(d => d.id === id)
          if (!isOpen) return state
          return { activeDocumentId: id }
        },
        false,
        'setActiveDocument'
      ),

      closeAllDocuments: () => set(
        // Plan 40 / #794: clear all three transient clause-selection fields
        // alongside the documents. Without this, a stale selectedClause
        // survives resetAllStores() and re-pushes a clauseActions view onto
        // a freshly-cleared rightPanelViewStack on next RightPanelContainer
        // mount (the useRef-based push-detect starts fresh).
        {
          openDocuments: [],
          activeDocumentId: null,
          selectedClause: null,
          selectedClauseIncludeDescendants: false,
          pendingClauseId: null,
            scrollToClauseId: null,
        },
        false,
        'closeAllDocuments'
      ),

      closeOtherDocuments: (keepId) => set(
        (state) => {
          const kept = state.openDocuments.find(d => d.id === keepId)
          // Plan 40 / #794: see closeDocument comment — same scoping rules.
          const selectedSurvives = state.selectedClause?.documentId === keepId
          return {
            openDocuments: kept ? [kept] : [],
            activeDocumentId: kept ? keepId : null,
            pendingClauseId: null,
            scrollToClauseId: null,
            ...(state.selectedClause && !selectedSurvives && {
              selectedClause: null,
              selectedClauseIncludeDescendants: false,
            }),
          }
        },
        false,
        'closeOtherDocuments'
      ),

      closeMatterDocuments: (matterId) => set(
        (state) => {
          const newDocuments = state.openDocuments.filter(d => d.matterId !== matterId)
          const activeStillOpen = newDocuments.some(d => d.id === state.activeDocumentId)
          // Plan 40 / #794: see closeDocument comment — same scoping rules.
          const selectedDocStillOpen = newDocuments.some(d => d.id === state.selectedClause?.documentId)

          return {
            openDocuments: newDocuments,
            activeDocumentId: activeStillOpen
              ? state.activeDocumentId
              : newDocuments[0]?.id ?? null,
            pendingClauseId: null,
            scrollToClauseId: null,
            ...(state.selectedClause && !selectedDocStillOpen && {
              selectedClause: null,
              selectedClauseIncludeDescendants: false,
            }),
          }
        },
        false,
        'closeMatterDocuments'
      ),

      switchMatter: (fromMatterId, toMatterId) => set(
        (state) => {
          // Plan 42 Task 1 — filter on both save and restore so cross-matter
          // contamination from any source (orchestration gap, off-path
          // openDocument callers, legacy persisted snapshots) cannot
          // accumulate or replay.
          const newMatterTabState = { ...state.matterTabState }

          if (fromMatterId) {
            // Save: only persist tabs whose own matterId field matches
            // fromMatterId. Refuses to bake contamination into the per-matter
            // snapshot.
            const cleanOpen = state.openDocuments.filter(d => d.matterId === fromMatterId)
            const cleanActive = cleanOpen.some(d => d.id === state.activeDocumentId)
              ? state.activeDocumentId
              : null
            newMatterTabState[fromMatterId] = {
              openDocuments: cleanOpen,
              activeDocumentId: cleanActive,
            }
          }

          // toMatterId falsy ('' or null) → clear live tabs without restoring.
          // Never index matterTabState[''] — empty-string keys would expose
          // any legacy or contaminated empty-key snapshot (e.g. from a
          // historical bug where switchMatter was called with no source
          // matter). Symmetric to the save side: only restore tabs whose
          // matterId matches toMatterId. Self-heals existing corrupt
          // snapshots on first visit — no migration needed.
          const savedRaw = toMatterId ? newMatterTabState[toMatterId] : undefined
          const restoredOpen = toMatterId
            ? (savedRaw?.openDocuments.filter(d => d.matterId === toMatterId) ?? [])
            : []
          const restoredActive = toMatterId
            ? (restoredOpen.some(d => d.id === savedRaw?.activeDocumentId)
                ? savedRaw!.activeDocumentId
                : (restoredOpen[0]?.id ?? null))
            : null

          return {
            matterTabState: newMatterTabState,
            openDocuments: restoredOpen,
            activeDocumentId: restoredActive,
            selectedClause: null,
            selectedClauseIncludeDescendants: false,
            pendingClauseId: null,
            scrollToClauseId: null,
          }
        },
        false,
        'switchMatter'
      ),

      // Plan 42 Task 2 — mount-time validation primitive. matterId === null
      // means "no matter selected"; hard-clear every tab. CRITICAL: do NOT
      // use filter(d => d.matterId === matterId) when matterId is null —
      // that would *keep* any tab whose persisted matterId is literally null.
      // The TS type says matterId: string, but this entire action exists
      // because we don't trust persisted state. Hard-clear is the only safe
      // behaviour.
      validateLiveTabs: (matterId) => set((state) => {
        const cleanOpen = matterId == null
          ? []
          : state.openDocuments.filter(d => d.matterId === matterId)
        if (cleanOpen.length === state.openDocuments.length) return state // no-op fast path

        // When matterId is null, every tab is dropped (cleanOpen is []), so
        // every open id goes into droppedIds. When matterId is a string, the
        // dropped set is the inverse of the keep filter above.
        const droppedIds = new Set(
          (matterId == null
            ? state.openDocuments
            : state.openDocuments.filter(d => d.matterId !== matterId)
          ).map(d => d.id)
        )
        const cleanActive = cleanOpen.some(d => d.id === state.activeDocumentId)
          ? state.activeDocumentId
          : (cleanOpen[0]?.id ?? null)

        // Mirror closeDocument's dependent-state cleanup: drop selectedClause
        // if its document was dropped; pendingClauseId / scrollToClauseId are
        // one-shot and harmless to reset unconditionally.
        const selectedClauseDropped =
          state.selectedClause != null && droppedIds.has(state.selectedClause.documentId)

        return {
          openDocuments: cleanOpen,
          activeDocumentId: cleanActive,
          pendingClauseId: null,
          scrollToClauseId: null,
          ...(selectedClauseDropped && {
            selectedClause: null,
            selectedClauseIncludeDescendants: false,
          }),
        }
      }, false, 'validateLiveTabs'),

      reorderDocuments: (fromIndex, toIndex) => set(
        (state) => {
          const newDocuments = [...state.openDocuments]
          const [removed] = newDocuments.splice(fromIndex, 1)
          newDocuments.splice(toIndex, 0, removed)
          return { openDocuments: newDocuments }
        },
        false,
        'reorderDocuments'
      ),

      // Task 220: Clause selection for context-aware quick actions.
      // Plan 171: a new focal selection resets the section toggle — opt-in
      // every time via selectSection or setSelectedClauseIncludeDescendants.
      selectClause: (clause) => set(
        { selectedClause: clause, selectedClauseIncludeDescendants: false },
        false,
        'selectClause'
      ),

      clearClauseSelection: () => set(
        // Plan 40 / #794: also clear pendingClauseId. The existing call sites
        // (IssuePageOverlay.tsx:482,668) pair this with resetRightPanelToChat
        // as part of a transient-state normalisation — dropping a stale
        // pending-scroll id alongside is consistent with that intent.
        { selectedClause: null, selectedClauseIncludeDescendants: false, pendingClauseId: null, scrollToClauseId: null },
        false,
        'clearClauseSelection'
      ),

      // Plan 171: atomic focal + includeDescendants=true in a single setter.
      // Used by the section header "Select section" button so the call order
      // (selectClause's reset vs. setting the flag true) is never fragile.
      selectSection: (clause) => set(
        {
          selectedClause: clause,
          selectedClauseIncludeDescendants: clause !== null,
        },
        false,
        'selectSection'
      ),

      setSelectedClauseIncludeDescendants: (value) => set(
        { selectedClauseIncludeDescendants: value },
        false,
        'setSelectedClauseIncludeDescendants'
      ),

      // Task 238: Pending clause scroll
      setPendingClauseId: (clauseId) => set(
        { pendingClauseId: clauseId },
        false,
        'setPendingClauseId'
      ),

      // Plan 191 / Issue #796: scroll-only — Desk.tsx's reactive effect
      // queries [data-clause-id] and scrolls without touching selection
      // or focused issue.
      setScrollToClauseId: (clauseId) => set(
        { scrollToClauseId: clauseId },
        false,
        'setScrollToClauseId'
      ),
    }),
    {
      name: 'taylaw-document-tabs',
      // Plan 167 Task 1.0: bumped from version 0 (default) to version 1.
      // The migrate filters out workflow-result tabs that may be persisted
      // from before this version's deploy.
      // Plan 42 Task 4: bumped to version 2. Adds foreign-matter snapshot
      // cleanup — per-matter snapshots in matterTabState shed any tab
      // whose own matterId disagrees with the snapshot's key.
      version: 2,
      partialize: (state) => ({
        openDocuments: state.openDocuments,
        activeDocumentId: state.activeDocumentId,
        matterTabState: state.matterTabState,
      }),
      migrate: (persistedState, version) => documentStorePersistMigrate(persistedState, version),
    }
    ),
    { name: 'DocumentStore' }
  )
)

/**
 * Plan 167 (Task 1.0): persist migration for workflow-result tab removal.
 *
 * Filters workflow-result tabs out of openDocuments and every per-matter
 * tab snapshot. Nulls activeDocumentId if it pointed at a removed tab.
 * Tolerates malformed shapes from old localStorage payloads.
 *
 * Exported for unit testing.
 */
export function documentStorePersistMigrate(persistedState: any, version: number): any {
  if (persistedState === null || persistedState === undefined) return persistedState
  if (typeof persistedState !== 'object') return persistedState

  // Plan 42 Task 4 — staged version cleanup. Each version's cleanup is an
  // independent pass; a v0 payload going through v1 then v2 gets both
  // applied. Don't add a blanket `if (version >= N) return` early exit
  // because it would skip later stages for old data.
  let state = persistedState
  if (version < 1) state = migrateV0ToV1(state)
  if (version < 2) state = migrateV1ToV2(state)
  return state
}

/**
 * v0 → v1: filter workflow-result tabs from openDocuments and every
 * matterTabState snapshot. Nulls activeDocumentId if it pointed at a
 * removed tab. Tolerates malformed shapes.
 */
function migrateV0ToV1(persistedState: any): any {
  const isStale = (d: any) => d?.type === 'workflow-result'

  const openDocuments = Array.isArray(persistedState.openDocuments)
    ? persistedState.openDocuments.filter((d: any) => !isStale(d))
    : []

  const openIds = new Set(openDocuments.map((d: any) => d.id))
  const activeDocumentId = persistedState.activeDocumentId && openIds.has(persistedState.activeDocumentId)
    ? persistedState.activeDocumentId
    : null

  const matterTabState: Record<string, any> = {}
  const rawMatterTabState = persistedState.matterTabState
  if (rawMatterTabState && typeof rawMatterTabState === 'object') {
    for (const [matterId, snap] of Object.entries(rawMatterTabState as Record<string, any>)) {
      if (!snap || typeof snap !== 'object') continue
      const snapOpen = Array.isArray((snap as any).openDocuments)
        ? (snap as any).openDocuments.filter((d: any) => !isStale(d))
        : []
      const snapOpenIds = new Set(snapOpen.map((d: any) => d.id))
      const snapActive = (snap as any).activeDocumentId && snapOpenIds.has((snap as any).activeDocumentId)
        ? (snap as any).activeDocumentId
        : null
      matterTabState[matterId] = {
        ...(snap as any),
        openDocuments: snapOpen,
        activeDocumentId: snapActive,
      }
    }
  }

  return {
    ...persistedState,
    openDocuments,
    activeDocumentId,
    matterTabState,
  }
}

/**
 * v1 → v2 (Plan 42 Task 4): foreign-matter snapshot cleanup. For each entry
 * in matterTabState[key], drop any tab whose own `matterId !== key`, then
 * null `activeDocumentId` if it pointed at a dropped tab.
 *
 * Top-level `openDocuments` is NOT touched here: there's no `selectedMatterId`
 * available at migrate time to validate against. Task 2's `validateLiveTabs`
 * handles them at mount once the matter context is known.
 */
function migrateV1ToV2(persistedState: any): any {
  const matterTabState: Record<string, any> = {}
  const rawMatterTabState = persistedState.matterTabState
  if (rawMatterTabState && typeof rawMatterTabState === 'object') {
    for (const [matterId, snap] of Object.entries(rawMatterTabState as Record<string, any>)) {
      if (!snap || typeof snap !== 'object') continue
      const snapOpen = Array.isArray((snap as any).openDocuments)
        ? (snap as any).openDocuments.filter((d: any) => d?.matterId === matterId)
        : []
      const snapOpenIds = new Set(snapOpen.map((d: any) => d.id))
      const snapActive = (snap as any).activeDocumentId && snapOpenIds.has((snap as any).activeDocumentId)
        ? (snap as any).activeDocumentId
        : null
      matterTabState[matterId] = {
        ...(snap as any),
        openDocuments: snapOpen,
        activeDocumentId: snapActive,
      }
    }
  }

  return {
    ...persistedState,
    matterTabState,
  }
}

// Selector helpers
export const documentSelectors = {
  activeDocument: (state: DocumentStore) =>
    state.openDocuments.find(d => d.id === state.activeDocumentId) ?? null,

  documentCount: (state: DocumentStore) => state.openDocuments.length,

  isDocumentOpen: (id: string) => (state: DocumentStore) =>
    state.openDocuments.some(d => d.id === id),

  matterDocuments: (matterId: string) => (state: DocumentStore) =>
    state.openDocuments.filter(d => d.matterId === matterId),

  // Task 220: Selected clause for context-aware quick actions
  selectedClause: (state: DocumentStore) => state.selectedClause,

  // Plan 131: Direct access to persisted activeDocumentId for clause prefetch
  activeDocumentId: (state: DocumentStore) => state.activeDocumentId,

  // Task 220: Get active workflow type from the active document
  activeWorkflowType: (state: DocumentStore) => {
    const activeDoc = state.openDocuments.find(d => d.id === state.activeDocumentId)
    return activeDoc?.workflowType ?? null
  },
}
